import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ParentLayout from '../components/ParentLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  GraduationCap, Download, Filter, Search, Users, School,
  TrendingUp, TrendingDown, Calendar, ChevronDown, Star,
  FileText, Building, UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAcademicsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [schools, setSchools] = useState([]);
  
  // Filters
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('reports'); // 'reports', 'by-school', 'by-team'

  useEffect(() => {
    fetchData();
  }, [selectedTeam, selectedSchool, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch reports with filters
      const params = new URLSearchParams();
      if (selectedTeam !== 'all') params.append('team_id', selectedTeam);
      if (selectedSchool !== 'all') params.append('school', selectedSchool);
      if (dateRange !== 'all') params.append('date_range', dateRange);
      
      const [reportsRes, teamsRes, schoolsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/academics/reports?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/accountability/teams`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/academics/schools`, { withCredentials: true })
      ]);
      
      setReports(reportsRes.data.reports || []);
      setStats(reportsRes.data.stats || null);
      setTeams(teamsRes.data.teams || []);
      setSchools(schoolsRes.data.schools || []);
    } catch (err) {
      console.error('Failed to fetch academic data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTeam !== 'all') params.append('team_id', selectedTeam);
      if (selectedSchool !== 'all') params.append('school', selectedSchool);
      if (dateRange !== 'all') params.append('date_range', dateRange);
      
      const response = await axios.get(
        `${API_URL}/api/admin/academics/export/csv?${params}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `academics_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTeam !== 'all') params.append('team_id', selectedTeam);
      if (selectedSchool !== 'all') params.append('school', selectedSchool);
      if (dateRange !== 'all') params.append('date_range', dateRange);
      
      const response = await axios.get(
        `${API_URL}/api/admin/academics/export/pdf?${params}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `academics_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report');
    }
  };

  const filteredReports = reports.filter(report => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      report.player_name?.toLowerCase().includes(query) ||
      report.teacher_name?.toLowerCase().includes(query) ||
      report.school?.toLowerCase().includes(query)
    );
  });

  const getBehaviorColor = (score) => {
    if (score >= 4) return 'text-green-600 bg-green-50';
    if (score >= 3) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getAcademicBadge = (status) => {
    if (status === 'on_track') {
      return <Badge className="bg-green-100 text-green-700">On Track</Badge>;
    }
    return <Badge className="bg-orange-100 text-orange-700">Needs Work</Badge>;
  };

  const renderStars = (score) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${i <= score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  // Group reports by school
  const reportsBySchool = reports.reduce((acc, report) => {
    const school = report.school || 'Unknown School';
    if (!acc[school]) acc[school] = [];
    acc[school].push(report);
    return acc;
  }, {});

  // Group reports by team
  const reportsByTeam = reports.reduce((acc, report) => {
    const team = report.team_name || 'Unassigned';
    if (!acc[team]) acc[team] = [];
    acc[team].push(report);
    return acc;
  }, {});

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-navy uppercase tracking-tight flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-gold" />
              Academics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">View and export student academic reports from teacher feedback</p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
            <Button
              onClick={handleExportPDF}
              className="bg-navy hover:bg-navy/90 flex items-center gap-2"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stats.total_reports}</p>
                  <p className="text-sm text-gray-500">Total Reports</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.on_track_percent}%</p>
                  <p className="text-sm text-gray-500">On Track</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stats.avg_behavior?.toFixed(1)}</p>
                  <p className="text-sm text-gray-500">Avg Behavior</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stats.unique_students}</p>
                  <p className="text-sm text-gray-500">Students Reported</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setViewMode('reports')}
                className={`px-4 py-2 text-sm font-medium ${viewMode === 'reports' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                All Reports
              </button>
              <button
                onClick={() => setViewMode('by-school')}
                className={`px-4 py-2 text-sm font-medium ${viewMode === 'by-school' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                By School
              </button>
              <button
                onClick={() => setViewMode('by-team')}
                className={`px-4 py-2 text-sm font-medium ${viewMode === 'by-team' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                By Team
              </button>
            </div>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by player, teacher, or school..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Team Filter */}
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="all">All Teams</option>
              {teams.filter(t => t.player_count > 0).map(team => (
                <option key={team.team_id} value={team.team_id}>{team.name}</option>
              ))}
            </select>

            {/* School Filter */}
            <select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="all">All Schools</option>
              {schools.map(school => (
                <option key={school} value={school}>{school}</option>
              ))}
            </select>

            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="all">All Time</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
          </div>
        ) : viewMode === 'reports' ? (
          /* All Reports View */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">School</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Team</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Teacher</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Academic</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Behavior</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Week</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Comments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredReports.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        No reports found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredReports.map((report, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{report.player_name}</p>
                          <p className="text-xs text-gray-500">Grade {report.grade}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{report.school || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{report.team_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{report.teacher_name}</td>
                        <td className="px-4 py-3">{getAcademicBadge(report.academic_status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {renderStars(report.behavior_score)}
                            <span className={`text-sm font-medium px-2 py-0.5 rounded ${getBehaviorColor(report.behavior_score)}`}>
                              {report.behavior_score}/5
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{report.week_of}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" title={report.comments}>
                          {report.comments || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : viewMode === 'by-school' ? (
          /* By School View */
          <div className="space-y-6">
            {Object.keys(reportsBySchool).length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
                No reports found.
              </div>
            ) : (
              Object.entries(reportsBySchool).map(([school, schoolReports]) => {
                const avgBehavior = schoolReports.reduce((sum, r) => sum + r.behavior_score, 0) / schoolReports.length;
                const onTrackCount = schoolReports.filter(r => r.academic_status === 'on_track').length;
                const onTrackPercent = Math.round((onTrackCount / schoolReports.length) * 100);
                
                return (
                  <div key={school} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Building className="w-5 h-5 text-navy" />
                        <h3 className="font-semibold text-navy">{school}</h3>
                        <Badge className="bg-navy/10 text-navy">{schoolReports.length} reports</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-600">{onTrackPercent}% On Track</span>
                        <span className="text-gray-600">Avg Behavior: {avgBehavior.toFixed(1)}/5</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {schoolReports.map((report, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900">{report.player_name}</p>
                              {getAcademicBadge(report.academic_status)}
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">by {report.teacher_name}</span>
                              {renderStars(report.behavior_score)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* By Team View */
          <div className="space-y-6">
            {Object.keys(reportsByTeam).length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
                No reports found.
              </div>
            ) : (
              Object.entries(reportsByTeam).map(([team, teamReports]) => {
                const avgBehavior = teamReports.reduce((sum, r) => sum + r.behavior_score, 0) / teamReports.length;
                const onTrackCount = teamReports.filter(r => r.academic_status === 'on_track').length;
                const onTrackPercent = Math.round((onTrackCount / teamReports.length) * 100);
                
                return (
                  <div key={team} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-navy" />
                        <h3 className="font-semibold text-navy">{team}</h3>
                        <Badge className="bg-navy/10 text-navy">{teamReports.length} reports</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-600">{onTrackPercent}% On Track</span>
                        <span className="text-gray-600">Avg Behavior: {avgBehavior.toFixed(1)}/5</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {teamReports.map((report, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900">{report.player_name}</p>
                              {getAcademicBadge(report.academic_status)}
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">{report.school || 'No school'}</span>
                              {renderStars(report.behavior_score)}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">by {report.teacher_name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminAcademicsPage;
